/**
 * terrain_lod — which mesh resolution a Commons terrain chunk should carry,
 * and when a resident chunk should be rebuilt to change it.
 *
 * WHY THIS EXISTS (2026-09-14). The seg count used to be picked once, at the
 * moment the chunk was first requested, and never revisited: EnvironmentModule
 * skipped any key already in `chunkMeshes`, and chunks are only evicted when
 * they leave the streaming window. A chunk that streamed in at the window edge
 * therefore kept seg 24 — or seg 10 — while the player walked onto it and
 * stood on it. At the top tiers the window is ~10 km across, so eviction is
 * rare and the defect is WORSE the higher the graphics tier.
 *
 * It was not only cosmetic: cave mouths are cut only on the seg-48 path
 * (EnvironmentModule's `if (seg < CHUNK_SEG_NEAR) return`), so a cave whose
 * chunk first streamed in from a distance had no opening at all.
 *
 * The ladder is kept here as pure functions so it can be exercised without
 * booting the module (verify:terrain-lod).
 */
import { CHUNK_SEG_NEAR, CHUNK_SEG_FAR, CHUNK_SEG_HORIZON, CHUNK_HORIZON_RING } from '../config.js';

/**
 * The seg a chunk at Chebyshev ring `cheb` is designed to carry.
 *
 * Larger seg = finer mesh (CHUNK_SEG_HORIZON 10 < CHUNK_SEG_FAR 24 <
 * CHUNK_SEG_NEAR 48). {@link refineChunkSeg} relies on that ordering to tell
 * a refinement from a coarsening, so the constants must stay ordered.
 */
export function segForRing(cheb: number, nearSeg: number = CHUNK_SEG_NEAR): number {
  // R8a (2026-09-25): high / ultra may ask the near ring (the 9 chunks the
  // colliders cover) for seg 96 (2.7 m cells, +~124k triangles) — the
  // `groundNearSeg` flag. The ladder keeps its order (96 > 48 > 24 > 10).
  return cheb <= 1 ? Math.max(CHUNK_SEG_NEAR, nearSeg) : cheb <= CHUNK_HORIZON_RING ? CHUNK_SEG_FAR : CHUNK_SEG_HORIZON;
}

/**
 * The seg a RESIDENT chunk should be rebuilt to, or `null` to leave it alone.
 *
 * Refinement happens the moment the chunk reaches its ring — that is the whole
 * point: the player must not stand on seg-10 ground, or walk up to a cave
 * mouth that was never cut.
 *
 * Coarsening waits until the chunk is a full ring PAST the boundary. The ring
 * test is integer chunk distance, so without that dead band a player pacing
 * back and forth across a single 256 m chunk edge would rebuild the entire
 * boundary ring on every crossing. The band costs a one-ring halo of
 * over-detailed chunks (at worst ~25 seg-48 chunks where the ladder nominally
 * wants 9), which is far cheaper than the rebuild churn it prevents.
 */
export function refineChunkSeg(cheb: number, resident: number, nearSeg: number = CHUNK_SEG_NEAR): number | null {
  const want = segForRing(cheb, nearSeg);
  if (want === resident) return null;
  if (want > resident) return want;                  // finer: act at the boundary
  // Coarser: only once the ring BEHIND this one also disagrees (dead band).
  return segForRing(cheb - 1, nearSeg) === resident ? null : want;
}
